import type { Package } from './package';
import type { StorageArea, StoragePlace } from '../../storageAreas/domain/storageArea';
import { PlaceAccessDirection } from '../../storageAreas/domain/placeAccessDirection';

const DEFAULT_DECREMENT_PER_BLOCKER = -10;

export interface PackageAccessibilityRating {
  score: number;
  blockingPackageCount: number;
  blockingPackageIds: string[];
}

export function computePackageAccessibilityRatings(
  packages: Package[],
  storageAreas: StorageArea[],
): Map<string, PackageAccessibilityRating> {
  const ratings = new Map<string, PackageAccessibilityRating>();
  const storageAreasById = new Map(storageAreas.map((area) => [area.id, area]));

  for (const target of packages) {
    const blockers = new Map<string, number>();
    const targetAreaId = target.packageStorageAreaId;

    if (!targetAreaId || target.occupiedStoragePlaceIds.length === 0) {
      ratings.set(target.id, { score: 100, blockingPackageCount: 0, blockingPackageIds: [] });
      continue;
    }

    const area = storageAreasById.get(targetAreaId);
    const places = area?.packageStorageData?.places ?? area?.packagePlaceData?.places ?? [];
    if (places.length === 0) {
      ratings.set(target.id, { score: 100, blockingPackageCount: 0, blockingPackageIds: [] });
      continue;
    }

    const placeById = new Map(places.map((place) => [place.id, place]));
    const targetPlaceIds = new Set(target.occupiedStoragePlaceIds);
    const targetPlaces = target.occupiedStoragePlaceIds
      .map((id) => placeById.get(id))
      .filter((place): place is StoragePlace => !!place);

    const targetStackLevel = target.stackLevel ?? 0;

    for (const candidate of packages) {
      if (candidate.id === target.id || candidate.packageStorageAreaId !== targetAreaId) {
        continue;
      }

      const candidatePlaces = candidate.occupiedStoragePlaceIds
        .map((id) => placeById.get(id))
        .filter((place): place is StoragePlace => !!place);
      if (candidatePlaces.length === 0) {
        continue;
      }

      const penalties: number[] = [];

      const candidateStackLevel = candidate.stackLevel ?? 0;
      const sharesPlace = candidate.occupiedStoragePlaceIds.some((id) => targetPlaceIds.has(id));
      if (sharesPlace && candidateStackLevel <= targetStackLevel) {
        // Packages underneath the target never block it.
        continue;
      }

      if (candidateStackLevel > targetStackLevel) {
        for (const sharedPlaceId of candidate.occupiedStoragePlaceIds) {
          if (!targetPlaceIds.has(sharedPlaceId)) continue;
          const place = placeById.get(sharedPlaceId);
          penalties.push(place?.accessibilityScoreDecrementPerBlocker ?? DEFAULT_DECREMENT_PER_BLOCKER);
        }
      }

      for (const targetPlace of targetPlaces) {
        if (targetPlace.column === null || targetPlace.row === null) continue;

        // A multi-row target is only blocked from beyond its own outermost row in this column.
        const targetPlacesInLine = targetPlaces.filter(
          (place) => place.column === targetPlace.column && place.zIndex === targetPlace.zIndex,
        );

        for (const candidatePlace of candidatePlaces) {
          if (candidatePlace.column === null || candidatePlace.row === null) continue;
          if (candidatePlace.column !== targetPlace.column) continue;
          if (candidatePlace.zIndex !== targetPlace.zIndex) continue;
          const candidateRow = candidatePlace.row;
          if (!targetPlacesInLine.every((place) => isHigherRow(place, candidateRow))) continue;

          penalties.push(targetPlace.accessibilityScoreDecrementPerBlocker ?? DEFAULT_DECREMENT_PER_BLOCKER);
          break;
        }
      }

      if (penalties.length === 0) {
        continue;
      }

      const mostRestrictivePenalty = penalties.reduce((min, value) => (value < min ? value : min), 0);
      blockers.set(candidate.id, mostRestrictivePenalty);
    }

    const totalPenalty = [...blockers.values()].reduce((sum, value) => sum + value, 0);
    const score = clampScore(100 + totalPenalty);
    ratings.set(target.id, {
      score,
      blockingPackageCount: blockers.size,
      blockingPackageIds: [...blockers.keys()],
    });
  }

  return ratings;
}

function isHigherRow(referencePlace: StoragePlace, candidateRow: number): boolean {
  const referenceRow = referencePlace.row;
  if (referenceRow === null) return false;

  const accessDirections = referencePlace.accessDirections;
  const hasFront = hasDirection(accessDirections, PlaceAccessDirection.Front);
  const hasBack = hasDirection(accessDirections, PlaceAccessDirection.Back);

  if (hasFront && !hasBack) {
    return candidateRow > referenceRow;
  }

  if (hasBack && !hasFront) {
    return candidateRow < referenceRow;
  }

  // Deterministic fallback for ambiguous directions such as All or Left/Right only.
  return candidateRow > referenceRow;
}

function hasDirection(value: number, direction: PlaceAccessDirection): boolean {
  return (value & direction) === direction;
}

function clampScore(value: number): number {
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}
